import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UploadImageBody,
  UploadImageData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UploadImageState
  extends MergeRequestState<UploadImageData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.uploadImage,
  isFetching: (state: ReduxState) => state.server.uploadImage.isFetching,
  data: (state: ReduxState) => state.server.uploadImage.data,
  error: (state: ReduxState) => state.server.uploadImage.error,
};

const initialState: UploadImageState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/uploadImage';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: UploadImageBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.uploadImage(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUploadImage {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const uploadImage: ServerUploadImage = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
